import React from 'react';

import { Group, Input, RHFSelect } from '@cowbell/ui';
import { Controller, useFormContext } from 'react-hook-form';

import { CleaveBase } from '../../../inputs/CleaveBase';
import { COUNTRY_CODES_SELECT_OPTIONS } from '../../../../i18n/i18n.language-config';
import { phoneFormatFor } from './org-phone-format';
import classes from './OrgPhoneField.module.css';

interface OrgPhoneFieldProps {
  readonly label: string;
  readonly name?: string;
}

/**
 * The business phone, in @cowbell/ui components: a dialling code beside the
 * number, as the design draws it.
 *
 * <p>The code drives two things -- which phone validation the form's schema
 * applies (`derivePhoneValidations` keys off `<name>CountryCode`) and how the
 * number is grouped as it is typed -- so the field name pair has to stay
 * `phoneNumber` / `phoneNumberCountryCode`.
 *
 * <p>The number goes through `Input` rather than `RHFTextInput` because it
 * needs Mantine's polymorphic `component` to keep the cleave.js grouping the
 * three MUI phone fields gave it; `TextInput` is not polymorphic.
 */
export const OrgPhoneField = ({
  label,
  name = 'phoneNumber',
}: OrgPhoneFieldProps) => {
  const { control, watch } = useFormContext();
  const countryCode = watch(`${name}CountryCode`) as string;

  return (
    <Group gap="xs" align="flex-end" wrap="nowrap">
      <RHFSelect
        name={`${name}CountryCode`}
        label={label}
        withAsterisk
        allowDeselect={false}
        data={COUNTRY_CODES_SELECT_OPTIONS}
        className={classes.countryCode}
      />
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <Input.Wrapper
            className={classes.number}
            error={fieldState.error?.message}
          >
            <Input
              component={CleaveBase}
              options={phoneFormatFor(countryCode)}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              aria-label="Phone number"
              placeholder="(555) 555-5555"
              error={Boolean(fieldState.error)}
            />
          </Input.Wrapper>
        )}
      />
    </Group>
  );
};

export default OrgPhoneField;
